import { useState } from 'react'
import { useAuth } from '@/components/Layout'
import { usePage } from '@inertiajs/react'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Home,
  Users,
  School,
  Settings,
  Package,
  DollarSign,
  UserCheck,
  Receipt,
  Calendar,
  Bell,
  Coins,
  UserPlus,
  Ticket,
  CalendarDays,
  GraduationCap,
  Waves,
  ArrowUpRight,
  BarChart3,
  ClipboardList,
  ChevronRight,
  type LucideIcon,
  ShoppingBag,
  Wallet,
} from 'lucide-react'
import bewaveLogo from '@/assets/logo-white.png'
import {
  isItemActive,
  persistOpenGroups,
  readOpenGroups,
  canSeeItem,
  type SidebarPageProps,
} from '@/lib/sidebarNav'

interface NavItem {
  title: string
  url: string
  icon: LucideIcon
  show?: boolean
  badge?: number
}

interface NavGroup {
  id: string
  title: string
  icon: LucideIcon
  items: NavItem[]
}

export function AppSidebar() {
  const { user } = useAuth()
  const page = usePage<SidebarPageProps>()
  const { state, setOpen, isMobile } = useSidebar()

  const currentPath = page.url
  const pageProps = page.props
  const isOwner = user?.role === 'owner'
  // O menu passa a perguntar "alcança este módulo?" em vez de "é o dono?".
  // As chaves são as mesmas de PanelPermissions::MODULES no servidor — se
  // divergirem, o item aparece e o portão nega, que é o erro mais barato dos dois.
  const can = (moduleKey: string) => canSeeItem(user, moduleKey)
  const isIconMode = !isMobile && state === 'collapsed'
  const enrollmentsEnabled = user?.school?.enrollments_enabled ?? false

  const dashboard: NavItem = {
    title: 'Dashboard',
    url: '/dashboard',
    icon: Home,
  }

  // Os destinos agrupados por área: opera-se em Operação, cadastra-se gente em
  // Pessoas, vende-se em Vendas, lança-se dinheiro em Financeiro, consulta-se em
  // Relatórios, e Escola guarda o que se configura uma vez e quase não se mexe.
  // "Pagamentos Professores" fica em Financeiro de propósito — lá se lança o
  // repasse; o par de consulta dele é "Aulas Dadas", em Relatórios.
  //
  // "Perfil da Escola" não está aqui: mora no menu do avatar, ao lado do sino.
  const navGroups: NavGroup[] = [
    {
      id: 'operacao',
      title: 'Operação',
      icon: Calendar,
      items: [
        {
          title: 'Agenda',
          url: '/lessons',
          icon: Calendar,
          show: can('agenda'),
        },
        {
          title: 'Eventos',
          url: '/events',
          icon: CalendarDays,
          show: can('eventos'),
        },
        {
          title: 'Equipamentos',
          url: '/equipments',
          icon: Waves,
          show: can('equipamentos'),
        },
      ],
    },
    {
      id: 'pessoas',
      title: 'Pessoas',
      icon: Users,
      items: [
        { title: 'Alunos', url: '/students', icon: Users, show: can('alunos') },
        { title: 'Equipe', url: '/team', icon: UserCheck, show: isOwner },
      ],
    },
    {
      id: 'vendas',
      title: 'Vendas',
      icon: Package,
      items: [
        {
          title: 'Pacotes de Aulas',
          url: '/lesson_packages',
          icon: Package,
          show: can('pacotes'),
        },
        {
          // Nem toda escola trabalha com matrícula. Quem usa liga o switch em
          // Configurações; para as demais a tela não polui o menu.
          title: 'Matriculas',
          url: '/enrollments',
          icon: GraduationCap,
          show: can('matriculas') && enrollmentsEnabled,
        },
        {
          title: 'Assinaturas',
          url: '/student_subscriptions',
          icon: UserCheck,
          show: can('assinaturas'),
        },
        { title: 'Cupons', url: '/coupons', icon: Ticket, show: can('cupons') },
        {
          title: 'Balcão',
          url: '/sales',
          icon: ShoppingBag,
          show: can('balcao'),
        },
      ],
    },
    {
      id: 'financeiro',
      title: 'Financeiro',
      icon: DollarSign,
      items: [
        { title: 'Caixa', url: '/financial', icon: DollarSign, show: isOwner },
        {
          title: 'Repasses',
          url: '/payouts',
          icon: ArrowUpRight,
          show: isOwner,
        },
        {
          title: 'Pagamentos Professores',
          url: '/teacher_payments',
          icon: Receipt,
          show: isOwner,
        },
        {
          title: 'Comissões',
          url: '/commissions',
          icon: Wallet,
          show: isOwner,
        },
        {
          // Só para quem comissiona: o dono usa a tela de apuração acima, e um gerente
          // sem comissão configurada não tem o que ver aqui.
          title: 'Minhas comissões',
          url: '/commissions/mine',
          icon: Wallet,
          show: !isOwner && Boolean(user?.commission_enabled),
        },
      ],
    },
    {
      id: 'relatorios',
      title: 'Relatórios',
      icon: BarChart3,
      items: [
        {
          title: 'Ocupação',
          url: '/lessons/occupancy',
          icon: BarChart3,
          show: can('relatorios'),
        },
        {
          title: 'Aulas Dadas',
          url: '/reports/lessons_given',
          icon: ClipboardList,
          show: can('relatorios'),
        },
      ],
    },
    {
      id: 'escola',
      title: 'Escola',
      icon: School,
      items: [
        {
          title: 'CRM',
          url: '/crm',
          icon: UserPlus,
          show: can('crm'),
          badge: pageProps.auth?.crm_new_retention_count || 0,
        },
        { title: 'WaveCoins', url: '/wave_coins', icon: Coins, show: isOwner },
        {
          title: 'Notificações Push',
          url: '/push_notifications',
          icon: Bell,
          show: can('notificacoes'),
        },
        {
          title: 'Configurações',
          url: '/settings',
          icon: Settings,
          show: isOwner,
        },
      ],
    },
  ]

  // Um grupo sem nenhum item visível não vira cabeçalho vazio. Vale tanto para o
  // gerente que só recebeu um módulo quanto para o grupo Vendas numa escola que não
  // usa matrículas — em ambos os casos o menu encolhe em vez de mostrar seção vazia.
  const visibleGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.show !== false),
    }))
    .filter((group) => group.items.length > 0)

  const activeGroupId = visibleGroups.find((group) =>
    group.items.some((item) => isItemActive(item.url, currentPath, pageProps)),
  )?.id

  // O grupo da rota atual nasce aberto. Como o DashboardLayout remonta a cada
  // navegação do Inertia, este inicializador roda de novo em cada página — é o
  // que faz o menu "seguir" quem navega, sem gravar isso no localStorage.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const stored = new Set(readOpenGroups())
    if (activeGroupId) stored.add(activeGroupId)
    return stored
  })

  const setGroupOpen = (groupId: string, open: boolean) => {
    const next = new Set(openGroups)
    if (open) {
      next.add(groupId)
    } else {
      next.delete(groupId)
    }
    setOpenGroups(next)
    persistOpenGroups(next)
  }

  const renderBadge = (count: number) =>
    count > 0 ? (
      <span className="ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
        {count}
      </span>
    ) : null

  // O Dashboard não mora em grupo nenhum — é o destino de volta.
  const renderTopItem = (item: NavItem) => {
    const isActive = isItemActive(item.url, currentPath, pageProps)

    return (
      <SidebarMenuItem
        key={item.title}
        className={isActive ? 'border-l-4 border-primary' : ''}
      >
        <SidebarMenuButton
          asChild
          isActive={isActive}
          tooltip={item.title}
          className={
            isActive
              ? 'bg-primary text-primary-foreground font-semibold hover:bg-primary hover:text-primary-foreground'
              : ''
          }
        >
          <a href={item.url}>
            <item.icon className="h-4 w-4" />
            <span>{item.title}</span>
          </a>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  const renderSubItem = (item: NavItem) => {
    const isActive = isItemActive(item.url, currentPath, pageProps)

    return (
      <SidebarMenuSubItem key={item.title}>
        <SidebarMenuSubButton
          asChild
          isActive={isActive}
          className={
            isActive
              ? 'bg-primary text-primary-foreground font-semibold hover:bg-primary hover:text-primary-foreground'
              : ''
          }
        >
          <a href={item.url}>
            <item.icon className="h-4 w-4" />
            <span className="truncate">{item.title}</span>
            {renderBadge(item.badge ?? 0)}
          </a>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    )
  }

  const renderGroup = (group: NavGroup) => {
    const isOpen = openGroups.has(group.id)
    const hasActiveChild = group.items.some((item) =>
      isItemActive(item.url, currentPath, pageProps),
    )
    // Fechado, o grupo herda os avisos dos filhos — senão o badge do CRM some.
    const collapsedBadge = isOpen
      ? 0
      : group.items.reduce((total, item) => total + (item.badge ?? 0), 0)

    return (
      <Collapsible
        key={group.id}
        asChild
        open={isOpen}
        onOpenChange={(next) => {
          // Com a sidebar recolhida os sub-itens estão escondidos por CSS, então
          // alternar o grupo não mostraria nada: abre a sidebar junto.
          if (isIconMode) {
            setOpen(true)
            setGroupOpen(group.id, true)
            return
          }
          setGroupOpen(group.id, next)
        }}
        className="group/collapsible"
      >
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              tooltip={group.title}
              className={
                !isOpen && hasActiveChild ? 'text-primary font-semibold' : ''
              }
            >
              <group.icon className="h-4 w-4" />
              <span className="truncate">{group.title}</span>
              <span className="ml-auto flex shrink-0 items-center gap-1.5">
                {renderBadge(collapsedBadge)}
                <ChevronRight className="h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </span>
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>{group.items.map(renderSubItem)}</SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    )
  }

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border p-5 group-data-[collapsible=icon]:hidden flex items-center justify-center">
        <img src={bewaveLogo} alt="BeeWave" className="h-10" />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {renderTopItem(dashboard)}
              {visibleGroups.map(renderGroup)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}
